import type { PartDefinition } from '@rogue/rackbuilder'
import { addSpacersToPlateStorage } from '../../../utils/plate-storage'
import { RackOptions } from '../../../RackTypes'
import { PowerRackBuilder } from './powerRackBuilder'
import { FTMaterialBuilder } from '../functionalTrainer/materials/ftMaterialBuilder'
import { FrontCrossmemberBuilder } from '../builders/frontCrossmemberBuilder'

/**
 * FTPowerRackBuilder — PowerRackBuilder subclass for Functional Trainer products.
 *
 * Overrides three extension hooks to inject FT-specific power rack behaviour
 * without polluting PowerRackBuilder with FT awareness:
 *
 *   shouldAddStorageRow    — also triggers for FT half racks that have plate storage
 *   shouldAddRMBrandingPlate — suppresses the RM branding plate on all FT products
 *                              except the Rhino Rack Trainer (which keeps it)
 *   onStoragePinsCreated   — adds FM storage spacers (RM line, non-LRLP only)
 *                            and attaches plate storage to the back upright on half racks
 *
 * Used by FunctionalTrainerBuilder as the base rack, replacing direct PowerRackBuilder usage.
 */
export class FTPowerRackBuilder extends PowerRackBuilder {
  constructor(config: RackOptions) {
    super(config)
    // Replace the base MaterialBuilder with FTMaterialBuilder so FML/FM strategy
    // overrides (e.g. black addon crossmember on FML HR) reach FrontCrossmemberBuilder
    this.materialBuilder = new FTMaterialBuilder(config)
    this.frontCrossmemberBuilder = new FrontCrossmemberBuilder(config, this.material, this.materialBuilder)
  }


  /** FT products with plate storage on a 4-post base still need addStorageRow to run. */
  protected shouldAddStorageRow(): boolean {
    return !!this.sixPost || !!this.config.plateStorage
  }

  /**
   * FT products never show the RM branding plate — the FML/FM carriage provides
   * its own branding. Exception: Rhino Rack Trainer keeps the plate.
   */
  protected shouldAddRMBrandingPlate(left: boolean): boolean {
    return this.config.rhinoRackTrainer === true && !left
  }

  /** Suppress spotter arms on half racks when a rack roller is configured — both use safety-low. */
  protected shouldAddSpotterArms(): boolean {
    return !(this.config.rackRoller && !this.config.sixPost)
  }

  /** FT j-cup placement depends on post count: back upright for six-post, front upright otherwise. */
  protected shouldPlaceJCupOnBackUpright(): boolean {
    return !!this.config.sixPost
  }

  /**
   * After storagePins are built:
   *   1. Add storage spacers for all FT variants except LRLP
   *   2. Attach plate storage to the back upright on FT half racks
   */
  protected onStoragePinsCreated(storagePins: PartDefinition[], backUpright: PartDefinition, left: boolean): void {
    if (!this.config.latAndLowRowOptions) {
      addSpacersToPlateStorage(this.config, this.material, this.getSideTransform(left), storagePins)
    }

    if (this.config.plateStorage && !this.config.sixPost) {
      backUpright.slots.storage = storagePins
    }
  }
}
